import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import type { OperationMode } from '@/services';
import { OperationModeSchedule } from '@features/shopautomation/types.ts';
import FormTagSelect from '@/components/form/FormTagSelect.tsx';
import FormTimePicker from '@/components/form/FormTimePicker.tsx';
import { required, ValidationRule } from '@core/utils/validation.ts';
import { LABEL_SX } from './OperationModesSchedulesEditor';
import { useModeTagState } from '@core/hooks/useModeTagState.ts';

interface ScheduleRowsProps {
    isMobile: boolean;
    operationModeSchedules: OperationModeSchedule[];
    filteredOperationModes: OperationMode[];
    timeRulesForIndex: (index: number) => ValidationRule[];
    onTimeChange: (index: number, newValue: DateTime | null) => void;
    onOperationModeChange: (index: number, value: string) => void;
    onAdd: () => void;
    onRemove: (index: number) => void;
}

const ScheduleRows: React.FC<ScheduleRowsProps> = ({
    isMobile,
    operationModeSchedules,
    filteredOperationModes,
    timeRulesForIndex,
    onTimeChange,
    onOperationModeChange,
    onAdd,
    onRemove
}) => {
    const { t } = useTranslation();
    const getModeTagState = useModeTagState();

    if (isMobile) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography sx={LABEL_SX}>{t('operationModeSchedule')}</Typography>

                {operationModeSchedules.map((oms, index) => (
                    <Box
                        key={oms.placeholderId || oms.id || index}
                        sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}
                    >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <FormTimePicker
                                name={'time_' + index}
                                disabled={index === 0}
                                value={DateTime.fromISO(oms.from)}
                                onChange={(newValue) => onTimeChange(index, newValue)}
                                ampm={false}
                                minutesStep={15}
                                rules={[required(t), ...timeRulesForIndex(index)]}
                                label={t('startTime')}
                            />
                        </Box>
                        <Box sx={{ flex: 1.8, minWidth: 0 }}>
                            <FormTagSelect
                                name={`operationMode_${index}`}
                                label={t('operationMode')}
                                value={oms?.operationMode || ''}
                                items={filteredOperationModes}
                                itemText="name"
                                itemValue="name"
                                getTagState={(item) => getModeTagState(item.name)}
                                onChange={(e) => onOperationModeChange(index, e)}
                                rules={[required(t)]}
                            />
                        </Box>
                        <IconButton
                            size="small"
                            onClick={index !== 0 ? () => onRemove(index) : undefined}
                            disabled={index === 0}
                            sx={{ mb: '3px', visibility: index !== 0 ? 'visible' : 'hidden' }}
                        >
                            <i className="bi bi-trash3" style={{ fontSize: '14px' }} />
                        </IconButton>
                    </Box>
                ))}

                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <IconButton size="small" onClick={onAdd}>
                        <i className="bi bi-plus-lg" style={{ fontSize: '14px' }} />
                    </IconButton>
                </Box>
            </Box>
        );
    }

    const HEADER_SX = {
        fontSize: '14px',
        fontWeight: 400,
        textTransform: 'uppercase',
        color: '#000'
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
            <Typography sx={{ ...LABEL_SX, pt: '2px' }}>{t('operationModeSchedule')}</Typography>

            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', minWidth: 0 }}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '160px 220px 32px 32px',
                        columnGap: 2,
                        rowGap: 1.5,
                        alignItems: 'start'
                    }}
                >
                    <Typography variant="caption" sx={{ ...HEADER_SX, gridColumn: 1, mb: -0.5 }}>
                        {t('startTime')}
                    </Typography>
                    <Typography variant="caption" sx={{ ...HEADER_SX, gridColumn: 2, mb: -0.5 }}>
                        {t('operationMode')}
                    </Typography>
                    <Box sx={{ gridColumn: '3 / span 2' }} />

                    {operationModeSchedules.map((oms, index) => (
                        <React.Fragment key={oms.placeholderId || oms.id || index}>
                            <FormTimePicker
                                name={'time_' + index}
                                value={DateTime.fromISO(oms.from)}
                                onChange={(newValue) => onTimeChange(index, newValue)}
                                ampm={false}
                                minutesStep={15}
                                rules={[required(t), ...timeRulesForIndex(index)]}
                                label={t('startTime')}
                                hideLabel
                                disabled={index === 0}
                            />

                            <FormTagSelect
                                name={`operationMode_${index}`}
                                label={t('operationMode')}
                                hideLabel
                                value={oms?.operationMode || ''}
                                items={filteredOperationModes}
                                itemText="name"
                                itemValue="name"
                                getTagState={(item) => getModeTagState(item.name)}
                                onChange={(e) => onOperationModeChange(index, e)}
                                rules={[required(t)]}
                            />

                            <IconButton size="small" onClick={onAdd} sx={{ mt: '3px' }}>
                                <i className="bi bi-plus-lg" style={{ fontSize: '14px' }} />
                            </IconButton>

                            <IconButton
                                size="small"
                                onClick={index !== 0 ? () => onRemove(index) : undefined}
                                disabled={index === 0}
                                sx={{ mt: '3px', visibility: index !== 0 ? 'visible' : 'hidden' }}
                            >
                                <i className="bi bi-trash3" style={{ fontSize: '14px' }} />
                            </IconButton>
                        </React.Fragment>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

export default ScheduleRows;
